import { supportedLocales } from "@/i18n";
import {
  AdvancedSearchFieldConfig,
  SearchOperator,
  StringFieldSearchConfig,
  SubmitableSortField,
  SubmittableSearchFormField,
} from "./advanced-search-fields-def";

/**
 * Applies the prisma where creation logic to a JSON.
 *
 * There is one major difference with the other data types:
 * Since the mapComparator function doesn't add the key to the created where clause
 * and is applied as a map, we are forced to return an OR clause as a single item,
 * which will later be skipped by the keyToNestedObject function.
 * @param paths The list of paths to apply the comparator to. Each path must be formatted
 * as an array of strings, thus matching with the way prisma handles json paths.
 * @param comparator
 * @param key
 * @param value
 * @returns An array of partial where clauses
 */
function mapJsonToArray(
  paths: string[][],
  comparator: SubmittableSearchFormField["comparator"],
  key: string,
  value: unknown,
) {
  let prismaComparator: string;
  switch (comparator) {
    case "contains":
      prismaComparator = "string_contains";
      break;
    // add more comparators here if needed
    default:
      throw new Error("Invalid comparator");
  }

  if (paths.length === 0) {
    throw new Error("No paths provided");
  }

  const whereClause = {
    OR: paths.map((path) =>
      keyToNestedObject(key, {
        path: path,
        [prismaComparator]: value,
        mode: "insensitive",
      }),
    ),
  };
  return whereClause;
}

export function mapComparator(
  type: SubmittableSearchFormField["type"],
  comparator: SubmittableSearchFormField["comparator"],
  key: string,
  value: any,
): object {
  if (type === "currency") {
    return { path: ["amount"], [comparator]: value * 100 };
  }

  if (type === "multilingual") {
    const paths = supportedLocales.map((locale) => [locale]);
    return mapJsonToArray(paths, comparator, key, value);
  }

  switch (comparator) {
    case "contains":
    case "equals":
    case "startsWith":
    case "endsWith":
      return {
        [comparator]: value,
        ...(type === "string" ? { mode: "insensitive" } : {}),
      };
    case "empty":
      return { equals: "" };
    case "notEquals":
      return {
        not: {
          equals: value,
          ...(type === "string" ? { mode: "insensitive" } : {}),
        },
      };
    case "notContains":
      return { not: { contains: value } };
    case "notEmpty":
      return { not: { equals: "" } };
    default:
      return { [comparator]: value };
  }
}

export function keyToNestedObject(key: string, value: unknown): object {
  if (
    typeof value === "object" &&
    value != null &&
    ["OR"].includes(Object.keys(value)[0])
  ) {
    return { ...value };
  }

  return key
    .split(".")
    .reverse()
    .reduce((acc, curr) => ({ [curr]: acc }), value) as object;
}

/**
 *
 * @param f
 * @returns undefined when the data is not coherent.
 *          This includes :
 *              - When field "value" is wrongly undefined
 */
function getValueKeyDependingOnType(f: SubmittableSearchFormField):
  | {
      value: unknown;
      key: string;
    }
  | undefined {
  switch (f.type) {
    case "number":
      return {
        value:
          Number.parseFloat(f.value?.toString() ?? "0") * (f.multiplyBy ?? 1),
        key: f.key,
      };
    case "enum": {
      if (f.useBooleans !== true) return { key: f.key, value: f.value };

      if (f.value == undefined) return undefined;

      return {
        key: f.value,
        value: true,
      };
    }
    default:
      return { key: f.key, value: f.value };
  }
}

/**
 *
 * @param f
 * @returns undefined when the data is not coherent
 */
export function advancedSearchFieldToPrismaWhere(
  f: SubmittableSearchFormField,
): object | undefined {
  const vk = getValueKeyDependingOnType(f);

  if (!vk) return undefined;

  const baseQuery = keyToNestedObject(
    vk.key.split(".").slice(-1)[0], // Get the last part of the key
    mapComparator(f.type, f.comparator ?? "equals", vk.key, vk.value),
  );

  // If there's no dot in the key, or no additional query, return simple query
  if (!vk.key.includes(".") || !f.additionalQuery?.length) {
    return keyToNestedObject(
      vk.key,
      mapComparator(f.type, f.comparator ?? "equals", vk.key, vk.value),
    );
  }

  // Get the parent key (everything before the last dot)
  const parentKey = vk.key.split(".").slice(0, -1).join(".");

  // Combine base query with additional queries under the parent key
  return keyToNestedObject(parentKey, {
    AND: [baseQuery, ...f.additionalQuery],
  });
}

export function advancedSearchToPrismaWhereContent(
  f: SubmittableSearchFormField[],
  mode: SearchOperator,
): object {
  const mappedFields = f
    .map(advancedSearchFieldToPrismaWhere)
    .filter((field) => field != undefined);

  if (mappedFields.length == 0) return {};
  if (mappedFields.length == 1) return mappedFields[0];

  return {
    [mode.toLocaleUpperCase()]: mappedFields,
  };
}

export function sortFieldsToPrismaOrderByContent(
  fields: SubmitableSortField[],
) {
  return fields.map((item) => ({
    [item.key]: item.sortMode,
  }));
}
export function globalSearchToPrismaWhereContent(
  flatAndFilteredConfig: AdvancedSearchFieldConfig<StringFieldSearchConfig>[],
  searchContent: string,
) {
  const orContent = flatAndFilteredConfig.map((c) => ({
    [c.key]: { contains: searchContent, mode: "insensitive" },
  }));

  return orContent.length > 0 ? { OR: orContent } : {};
}
